import {
  Alert,
  Button,
  Checkbox,
  Group,
  Modal,
  Select,
  Stack,
  TextInput,
} from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import { ApiError } from "@/lib/api/client";
import {
  type PatientDetail,
  type PatientUpdatePayload,
  updatePatient,
} from "@/lib/api/patients";

interface Props {
  patient: PatientDetail;
  opened: boolean;
  onClose: () => void;
}

function toDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function fromDate(value: Date | null): string | null {
  if (!value) return null;
  return value.toISOString().slice(0, 10);
}

export function PatientEditDialog({ patient, opened, onClose }: Props) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const form = useForm({
    initialValues: {
      family_name: patient.family_name,
      given_name: patient.given_name,
      father_name: patient.father_name ?? "",
      birth_date: toDate(patient.birth_date),
      sex: patient.sex,
      jshshir: patient.jshshir ?? "",
      passport_seriya: patient.passport_seriya ?? "",
      passport_number: patient.passport_number ?? "",
      address_region: patient.address_region ?? "",
      address_district: patient.address_district ?? "",
      address_full: patient.address_full ?? "",
      phone_primary: patient.phone_primary,
      phone_secondary: patient.phone_secondary ?? "",
      email: patient.email ?? "",
      workplace: patient.workplace ?? "",
      position: patient.position ?? "",
    },
    validate: {
      family_name: (v) => (v.trim().length > 0 ? null : t("validation.required")),
      given_name: (v) => (v.trim().length > 0 ? null : t("validation.required")),
      birth_date: (v) => (v ? null : t("validation.required")),
      phone_primary: (v) =>
        v.trim().length > 0 ? null : t("validation.required"),
      jshshir: (v) =>
        !v.trim() || /^\d{14}$/.test(v.replace(/\s+/g, ""))
          ? null
          : t("patients.validation.jshshir_format"),
    },
  });

  useEffect(() => {
    if (opened) {
      form.setValues({
        family_name: patient.family_name,
        given_name: patient.given_name,
        father_name: patient.father_name ?? "",
        birth_date: toDate(patient.birth_date),
        sex: patient.sex,
        jshshir: patient.jshshir ?? "",
        passport_seriya: patient.passport_seriya ?? "",
        passport_number: patient.passport_number ?? "",
        address_region: patient.address_region ?? "",
        address_district: patient.address_district ?? "",
        address_full: patient.address_full ?? "",
        phone_primary: patient.phone_primary,
        phone_secondary: patient.phone_secondary ?? "",
        email: patient.email ?? "",
        workplace: patient.workplace ?? "",
        position: patient.position ?? "",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, patient.id]);

  const mutation = useMutation({
    mutationFn: async (values: typeof form.values) => {
      const payload: PatientUpdatePayload = {
        family_name: values.family_name.trim(),
        given_name: values.given_name.trim(),
        father_name: values.father_name.trim() || null,
        birth_date: fromDate(values.birth_date) ?? undefined,
        sex: values.sex,
        jshshir: values.jshshir.trim() || null,
        passport_seriya: values.passport_seriya.trim() || null,
        passport_number: values.passport_number.trim() || null,
        address_region: values.address_region.trim() || null,
        address_district: values.address_district.trim() || null,
        address_full: values.address_full.trim() || null,
        phone_primary: values.phone_primary.trim(),
        phone_secondary: values.phone_secondary.trim() || null,
        email: values.email.trim() || null,
        workplace: values.workplace.trim() || null,
        position: values.position.trim() || null,
      };
      return updatePatient(patient.id, payload);
    },
    onSuccess: () => {
      notifications.show({
        color: "green",
        message: t("patients.updated", { defaultValue: "Bemor ma'lumotlari yangilandi" }),
      });
      queryClient.invalidateQueries({ queryKey: ["patient", patient.id] });
      queryClient.invalidateQueries({ queryKey: ["patients"] });
      onClose();
    },
  });

  const errorMessage = (() => {
    if (!mutation.error) return null;
    if (mutation.error instanceof ApiError) return mutation.error.message;
    return t("auth.network_error");
  })();

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={t("patients.edit_title", { defaultValue: "Bemor ma'lumotlarini tahrirlash" })}
      size="lg"
    >
      {errorMessage && (
        <Alert color="red" mb="md">
          {errorMessage}
        </Alert>
      )}
      <form onSubmit={form.onSubmit((v) => mutation.mutate(v))}>
        <Stack>
          <Group grow>
            <TextInput
              label={t("patients.fields.family_name", { defaultValue: "Familiya" })}
              {...form.getInputProps("family_name")}
            />
            <TextInput
              label={t("patients.fields.given_name", { defaultValue: "Ism" })}
              {...form.getInputProps("given_name")}
            />
          </Group>
          <TextInput
            label={t("patients.fields.father_name", { defaultValue: "Otasining ismi" })}
            {...form.getInputProps("father_name")}
          />
          <Group grow>
            <DateInput
              label={t("patients.fields.birth_date", { defaultValue: "Tug'ilgan sana" })}
              valueFormat="YYYY-MM-DD"
              {...form.getInputProps("birth_date")}
            />
            <Select
              label={t("patients.fields.sex", { defaultValue: "Jinsi" })}
              data={[
                { value: "male", label: t("patients.sex.male", { defaultValue: "Erkak" }) },
                { value: "female", label: t("patients.sex.female", { defaultValue: "Ayol" }) },
              ]}
              {...form.getInputProps("sex")}
            />
          </Group>
          <Group grow>
            <TextInput
              label={t("patients.fields.jshshir", { defaultValue: "JSHSHIR" })}
              placeholder="14 raqam"
              {...form.getInputProps("jshshir")}
            />
            <TextInput
              label={t("patients.fields.phone_primary", { defaultValue: "Asosiy telefon" })}
              placeholder="+998 90 123 45 67"
              {...form.getInputProps("phone_primary")}
            />
          </Group>
          <Group grow>
            <TextInput
              label={t("patients.fields.passport_seriya", { defaultValue: "Pasport seriyasi" })}
              {...form.getInputProps("passport_seriya")}
            />
            <TextInput
              label={t("patients.fields.passport_number", { defaultValue: "Pasport raqami" })}
              {...form.getInputProps("passport_number")}
            />
          </Group>
          <Group grow>
            <TextInput
              label={t("patients.fields.address_region", { defaultValue: "Viloyat" })}
              {...form.getInputProps("address_region")}
            />
            <TextInput
              label={t("patients.fields.address_district", { defaultValue: "Tuman" })}
              {...form.getInputProps("address_district")}
            />
          </Group>
          <TextInput
            label={t("patients.fields.address_full", { defaultValue: "To'liq manzil" })}
            {...form.getInputProps("address_full")}
          />
          <Group grow>
            <TextInput
              label={t("patients.fields.workplace", { defaultValue: "Ish joyi" })}
              {...form.getInputProps("workplace")}
            />
            <TextInput
              label={t("patients.fields.position", { defaultValue: "Lavozimi" })}
              {...form.getInputProps("position")}
            />
          </Group>
          <Group grow>
            <TextInput
              label={t("patients.fields.phone_secondary", { defaultValue: "Qo'shimcha telefon" })}
              {...form.getInputProps("phone_secondary")}
            />
            <TextInput
              label={t("patients.fields.email", { defaultValue: "Email" })}
              {...form.getInputProps("email")}
            />
          </Group>
          <Group justify="flex-end" mt="sm">
            <Button variant="subtle" color="gray" onClick={onClose}>
              {t("common.cancel", { defaultValue: "Bekor qilish" })}
            </Button>
            <Button type="submit" loading={mutation.isPending}>
              {t("common.save", { defaultValue: "Saqlash" })}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
